<script lang="ts">
  import Input from '$shared/atoms/Input/Input.svelte';
  import CustomCreate from './CustomCreate.svelte';
  import DropdownListItem from './DropdownListItem.svelte';
  import {
    NavRowKind,
    NoHighlight,
    getSelectableIndices,
    moveHighlight,
    scrollRowIntoView,
    type NavRow
  } from '$shared/helpers/listNavigation';

  interface Props {
    list?: CommonList[];
    maxheight?: boolean;
    canCreate?: boolean;
    canCreateOnInput?: boolean;
    placeholder?: string;
    oninputcreate?: (value: string) => void;
    onclick?: (item: CommonList) => void;
    oncreate?: (value: string) => void;
    /** Supplied by Dropdown so its trigger can aria-controls this list. */
    listId?: string;
  }

  let {
    list = [],
    maxheight = false,
    canCreate = false,
    canCreateOnInput = false,
    placeholder = '',
    oninputcreate,
    onclick,
    oncreate,
    listId
  }: Props = $props();

  const fallbackId = $props.id();
  const ownListId = $derived(listId || fallbackId);

  let searchValue = $state('');
  let inputElement: Input | undefined = $state();
  let container: HTMLElement | undefined = $state();
  const optionId = (index: number): string => `${ownListId}-opt-${index}`;
  let highlightedIndex = $state(NoHighlight);

  let filteredList = $derived(
    searchValue
      ? list.filter((item) => item?.label?.toLowerCase().includes(searchValue.toLowerCase()))
      : list
  );

  let isValueMatch = $derived(!!filteredList.find((item) => item.label === searchValue));

  let showCreateRow = $derived(
    canCreate && !!searchValue && (filteredList.length === 0 || !isValueMatch)
  );

  let rows: NavRow[] = $derived([
    ...filteredList.map((item) => ({
      kind: NavRowKind.Item,
      item,
      selectable: !item.disabled
    })),
    ...(showCreateRow
      ? [{ kind: NavRowKind.Create, item: { label: searchValue }, selectable: true }]
      : [])
  ]);

  let selectableIndices = $derived(getSelectableIndices(rows));

  export const focus = (): void => {
    inputElement?.focus();
  };

  export const reset = (): void => {
    searchValue = '';
    highlightedIndex = NoHighlight;
  };

  const setHighlight = (index: number): void => {
    if (index === NoHighlight) return;
    highlightedIndex = index;
    scrollRowIntoView(container, index);
  };

  /** Returns true when the key was consumed. */
  export const handleKeyDown = (e: KeyboardEvent): boolean => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      setHighlight(
        moveHighlight(highlightedIndex, selectableIndices, e.key === 'ArrowDown' ? 1 : -1)
      );
      return true;
    }
    if (e.key === 'Enter') {
      const row = rows[highlightedIndex];
      if (!row?.selectable) return false;
      if (row.kind === NavRowKind.Create) {
        onClickCustom();
      } else {
        onClickItem(row.item);
      }
      return true;
    }
    return false;
  };

  const onSearch = ({ value }: { value: string }): void => {
    searchValue = value;
    // Reset here rather than in an $effect on filteredList: an effect would
    // also fire when the highlight itself changes the derived list.
    highlightedIndex = NoHighlight;

    canCreateOnInput && oninputcreate?.(searchValue);
  };

  const onClickItem = (item: CommonList): void => {
    onclick?.(item);
  };

  const onClickCustom = (): void => {
    oncreate?.(searchValue);
  };
</script>

<div class="searchable-list" class:maxheight>
  <div class="search-input">
    <Input
      bind:this={inputElement}
      placeholder={placeholder || 'Search item'}
      role="combobox"
      ariaExpanded={true}
      ariaControls={ownListId}
      ariaActiveDescendant={highlightedIndex === NoHighlight
        ? undefined
        : optionId(highlightedIndex)}
      oninput={onSearch}
    />
  </div>

  <div bind:this={container} class="searchable-list-container" role="listbox" id={ownListId}>
    {#each rows as row, index}
      {#if row.kind === NavRowKind.Create}
        <CustomCreate
          value={searchValue}
          label={'Custom'}
          navIndex={index}
          optionId={optionId(index)}
          highlighted={index === highlightedIndex}
          onclick={onClickCustom}
        />
      {:else}
        <DropdownListItem
          item={row.item}
          navIndex={index}
          optionId={optionId(index)}
          highlighted={index === highlightedIndex}
          onclick={() => onClickItem(row.item)}
        />
      {/if}
    {/each}
    {#if rows.length === 0 && !canCreate}
      <div class="empty">No data found</div>
    {/if}
  </div>
</div>

<style>
  .searchable-list.maxheight .searchable-list-container {
    max-height: 100%;
  }
  .search-input {
    padding: 6px;
  }
  .searchable-list-container {
    width: 100%;
    display: inline-block;
    max-height: calc(300px - 50px);
    overflow: auto;
    /* Anchors offsetTop of the rows for scrollRowIntoView. */
    position: relative;
  }
  .empty {
    background-color: var(--grey-bg);
    line-height: 30px;
    padding: 0 8px;
    color: var(--grey-light);
  }
</style>
